import { mediatedEventPublish } from '@/platform/core/mediated-events';
import { IEditorOperations } from '@/platform/editor/i-editor-operations';
import { IFormOperations } from '@/platform/editor/i-form-operations';
import {
	IEditorIntegration,
	IEditorIntegrationPresence,
	IEditorValidationError,
	EditorEvent,
	IPresenceMessages,
} from '@studyportals/scholarships-editor-integration';

export class EditorIntegration implements IEditorIntegration, IEditorIntegrationPresence {
	public static readonly instance = new EditorIntegration();

	private _forms: IFormOperations[] = [];
	private _editor: IEditorOperations | undefined;

	private constructor() {
		// Restrict instantiation access
	}

	public set editor(value: IEditorOperations) {
		this._editor = value;
	}

	public formRegister(value: IFormOperations): void {
		this._forms.push(value);
	}

	public formsReset(): void {
		this._forms = [];
	}

	public async validate(): Promise<readonly IEditorValidationError[]> {
		const validationPromisses = this._forms.map((f) => f.validate());
		const validationErrors = await Promise.all(validationPromisses);

		return validationErrors.flat().map((e) => {
			return { editorTab: e.tab };
		});
	}

	public getObjectNamePlural(): string {
		return 'scholarships';
	}

	public getObjectNameSingular(): string {
		return 'scholarship';
	}

	public getTitle(): string {
		if (this._editor === undefined) {
			return 'N/A';
		}

		return this._editor.getTitle();
	}

	public getPublicUrl(): string {
		if (this._editor === undefined) {
			return '#';
		}

		return this._editor.getPublicUrl();
	}

	public save(): Promise<void> {
		if (this._editor === undefined) {
			throw new Error('The editor is not available');
		}

		return this._editor.save();
	}

	public getPresenceMessages(): IPresenceMessages {
		return {
			warningUnPublishAction: 'Are you sure you want to unpublish this scholarship?',
			warningUnPublishedStatus:
				"This scholarship is currently set to 'unpublished'. To publish the scholarship, set the status to 'published' and save.",
			errorCannotPublish: 'The scholarship cannot be published.',
		};
	}

	public isPublished(): boolean {
		if (this._editor === undefined) {
			return false;
		}

		return this._editor.isPublished();
	}

	public canPublish(): boolean {
		return true;
	}

	public publish(): void {
		if (this._editor === undefined) {
			throw new Error('The editor is not available');
		}

		this._editor.publish();
	}

	public unPublish(): void {
		if (this._editor === undefined) {
			throw new Error('The editor is not available');
		}

		this._editor.unPublish();
	}

	public notifyReady(): void {
		mediatedEventPublish(EditorEvent.READY);
	}

	public notifyFormDirty(): void {
		mediatedEventPublish(EditorEvent.FORM_DIRTY);
	}

	public notifyFormClean(): void {
		mediatedEventPublish(EditorEvent.FORM_CLEAN);
	}
}
